import { colorToHex } from '@client/utils/color';

/**
 * @typedef {import('../types').Brand} Brand
 * @typedef {import('../types').VirtualBackground} VirtualBackground
 */

/** @param {Brand} brand */
const gradient = (brand) => {
  const colorList = [brand.colorPrimary, brand.colorSecondary, brand.colorTertiary].filter(Boolean).map(colorToHex);

  return colorList.join(',');
};

/** @param {Brand} brand */
const logo = (brand) => (brand.logoUrl ? `{{ "${brand.logoUrl}" | img }}` : brand.companyName || '');

/*
+-----------------+
| [logo]          |
|                 |
| name            |
| title           |
+-----------------+
*/
/** @param {Brand} brand */
const left = (brand) => {
  return {
    background: gradient(brand),
    blur: 0,
    brightness: 1,
    foreground: '#FFFFFF',
    cells: [[logo(brand)], [], [], [], [], [], ['{{name}}', '{{title}}'], [], []]
  };
};

/**
+-----------------+
|          [logo] |
|                 |
| name            |
| title           |
+-----------------+
*/

/** @param {Brand} brand */
const right = (brand) => ({
  background: gradient(brand),
  blur: 0,
  brightness: 1,
  foreground: '#FFFFFF',
  cells: [[], [], [logo(brand)], [], [], [], ['{{name}}', '{{title}}'], [], []]
});

/*
+-----------------+
| [logo]          |
|                 |
|                 |
|                 |
+-----------------+
*/
/** @param {Brand} brand */
const company = (brand) => ({
  background: gradient(brand),
  blur: 0,
  brightness: 1,
  foreground: '#FFFFFF',
  cells: [[logo(brand)], [], [], [], [], [], [], [], []]
});

/** @param {Brand} brand */
const qr = (brand) => {
  return {
    background: gradient(brand),
    blur: 0,
    brightness: 1,
    foreground: '#FFFFFF',
    cells: [[logo(brand)], [], [`{{ "${brand.companyUrl}" | qr_code }}`], [], [], [], ['{{name}}', '{{title}}'], [], []]
  };
};

/*
+-----------------+
|                 |
|                 |
|           name  |
| [logo]    title |
+-----------------+
*/
/** @param {Brand} brand */
const footer = (brand) => ({
  background: gradient(brand),
  blur: 0,
  brightness: 1,
  foreground: '#FFFFFF',
  cells: [[], [], [], [], [], [], ['{{name}}', '{{title}}'], [], [logo(brand)]]
});

/*
+-----------------+
|                 |
|                 |
|                 |
|                 |
+-----------------+
*/
/** @param {Brand} brand */
const blank = (brand) => ({
  background: gradient(brand),
  blur: 0,
  brightness: 1,
  foreground: '#FFFFFF',
  cells: [[], [], [], [], [], [], [], [], []]
});

/** @type {Record<string, (brand: Brand) => VirtualBackground>} */
const templates = {
  left,
  right,
  company,
  qr,
  footer,
  blank
};

/**
 * Loads the virtual background template.
 * @param {string} name - The name of the template to use (e.g., 'classic', 'modern').
 * @param {Brand} brand - The brand object with theme colors, fonts, and assets.
 * @returns {VirtualBackground}
 */
export const loadBackgroundTemplate = (name, brand) => {
  const template = templates[name](brand);

  return template;
};

/**
 * Generates preview data for all available background templates.
 * @param {Brand} brand - The brand object with theme colors and assets.
 * @returns {Array} An array of template preview objects with full template data.
 */
export const loadNewBackgroundModalTemplates = (brand) => {
  const templateNames = Object.keys(templates);

  return templateNames.map((name) => ({
    id: name,
    name,
    width: 1920,
    height: 1080,
    ...templates[name](brand)
  }));
};
